import {
  all, call, put, select, takeEvery,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { IRestApiResponse } from 'interfaces';
import { selectAuthUser } from 'base/auth/selectors';
import { selectEnterprise } from 'base/core/selectors';
import { CoreSliceActions } from 'base/core/slice';
import { viewFormsActions } from './slice';
import {
  selectFormConfig,
  selectFormId,
  selectFormSettings,
  selectUserModuleCompletionId,
  selectUserSessionId,
} from './selector';
import API from './constants';
import {
  ISaveScormFormModalFormAction, IDeleteFormAction,
  IFormDocument, IFormFilterStateAction,
  FormBuilderVersion,
} from './interface';

export function* getFilteredFormsGenerator({ payload }: IFormFilterStateAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FORMS_BY_EXPERT.path}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&sortBy=${payload.sortBy}&formType=${payload.formType}${payload?.availability ? `&availability=${payload.availability}` : ''}`,
        method: API.GET_FORMS_BY_EXPERT.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(viewFormsActions.getFilteredFormsSucceeded(response.data));
    } else {
      yield put(viewFormsActions.getFilteredFormsFailed());
    }
  } catch (error) {
    yield put(viewFormsActions.getFilteredFormsFailed());
  }
}

export function* getFormGenerator(): any {
  try {
    const id = yield select(selectFormId);
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.GET_FORM_BY_ID.path}/${id}`, method: API.GET_FORM_BY_ID.method },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(viewFormsActions.getFormSucceeded(response.data as IFormDocument));
    } else {
      yield put(viewFormsActions.getFormFailed());
    }
  } catch (error) {
    yield put(viewFormsActions.getFormFailed());
  }
}

export function* getSubmittedFormGenerator(): any {
  try {
    const formId = yield select(selectFormId);
    const userSessionId = yield select(selectUserSessionId);
    const userModuleCompletionId = yield select(selectUserModuleCompletionId);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_USER_SUBMITED_FORM.path}/${formId}/${userSessionId}/${userModuleCompletionId}`,
        method: API.GET_USER_SUBMITED_FORM.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(viewFormsActions.getSubmittedFormSucceeded(response.data));
    } else {
      yield put(viewFormsActions.getSubmittedFormFailed());
    }
  } catch (error) {
    yield put(viewFormsActions.getSubmittedFormFailed());
  }
}

export function* createFormGenerator({ payload }: {
  payload: { resolve: any; reject: any };
  type: string;
}): any {
  try {
    const expert = yield select(selectAuthUser);
    const enterprise = yield select(selectEnterprise);
    const formSettings = yield select(selectFormSettings);
    const formConfig = yield select(selectFormConfig);
    const response: IRestApiResponse = yield call(
      request,
      API.POST_CREATE_FORM,
      {
        entId: enterprise.id,
        expertId: expert.id,
        ...formSettings,
        formConfig,
        version: FormBuilderVersion.V2,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(viewFormsActions.createFormSucceeded());
      yield payload.resolve();
    } else {
      yield put(viewFormsActions.createFormFailed());
      yield payload.reject(response?.message);
    }
  } catch (error) {
    yield put(viewFormsActions.createFormFailed());
    yield payload.reject('Form creation error. Please try again.');
  }
}

export function* saveScormFormGenerator({ payload }: ISaveScormFormModalFormAction) :any {
  try {
    const expert = yield select(selectAuthUser);
    const enterprise = yield select(selectEnterprise);
    const response: IRestApiResponse = yield call(
      request,
      payload.id ? API.POST_UPDATE_FORM : API.POST_CREATE_FORM,
      {
        entId: enterprise.id,
        expertId: expert.id,
        ...payload,
        version: FormBuilderVersion.V2,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(viewFormsActions.saveScormFormSucceeded());
      yield payload.resolve();
    } else {
      yield put(viewFormsActions.saveScormFormFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(viewFormsActions.saveScormFormFailed());
    yield payload.reject();
  }
}

export function* editFormsGenerator({ payload }: {
  payload: { resolve: any; reject: any };
  type: string;
}): any {
  try {
    const id = yield select(selectFormId);
    const formSettings = yield select(selectFormSettings);
    const formConfig = yield select(selectFormConfig);
    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_FORM,
      {
        id,
        ...formSettings,
        formConfig,
        version: FormBuilderVersion.V2,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(viewFormsActions.editFormSucceeded());
      yield payload.resolve();
    } else {
      yield put(viewFormsActions.editFormFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(viewFormsActions.editFormFailed());
    yield payload.reject();
  }
}

export function* markSubmitFormsGenerator(): any {
  try {
    const formId = yield select(selectFormId);
    const userSessionId = yield select(selectUserSessionId);
    const userModuleCompletionId = yield select(selectUserModuleCompletionId);
    const formConfig = yield select(selectFormConfig);

    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_SUNMIT_MARKS_FORM.path}?isExpert=true`,
        method: API.POST_SUNMIT_MARKS_FORM.method,
      },
      {
        submission: formConfig, formId, userSessionId, userModuleCompletionId,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(viewFormsActions.markSubmitFormsSucceeded());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Results has been published successfully.',
          btnText: 'Back',
        }),
      );
    } else {
      yield put(viewFormsActions.markSubmitFormsFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error publishing the result.',
          btnText: 'Try submitting again',
        }),
      );
    }
  } catch (error) {
    yield put(viewFormsActions.markSubmitFormsFailed());
    yield put(
      CoreSliceActions.displayAlert({
        type: 'error',
        message: 'There was an error publishing the result.',
        btnText: 'Try submitting again',
      }),
    );
  }
}

export function* deleteFormsGenerator({ payload }: IDeleteFormAction): any {
  try {
    const { id, bannerImgUrl } = payload;
    const response: IRestApiResponse = yield call(
      request,
      API.POST_DELETE_FORM,
      {
        id,
        bannerImgUrl,
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(viewFormsActions.deleteFormSucceeded(id));
    } else {
      yield put(viewFormsActions.deleteFormFailed());
    }
  } catch (error) {
    yield put(viewFormsActions.deleteFormFailed());
  }
}

export function* viewFormsSaga() {
  yield all([
    takeEvery(viewFormsActions.getFilteredForms.type, getFilteredFormsGenerator),
    takeEvery(viewFormsActions.getForm.type, getFormGenerator),
    takeEvery(viewFormsActions.createForm.type, createFormGenerator),
    takeEvery(viewFormsActions.saveScormForm.type, saveScormFormGenerator),
    takeEvery(viewFormsActions.editForm.type, editFormsGenerator),
    takeEvery(viewFormsActions.deleteForm.type, deleteFormsGenerator),
    takeEvery(viewFormsActions.getSubmittedForm.type, getSubmittedFormGenerator),
    takeEvery(viewFormsActions.markSubmitForms.type, markSubmitFormsGenerator),
  ]);
}

export default viewFormsSaga;
